import {
    ICachingStream,
    InvoiceGet,
    InvoiceOpenItem,
    SubscribeState,
    Ulid,
} from "@kase/my-marathon-communication";
import { ReadonlySignal, batch, computed, signal } from "@preact/signals";
import { upsert } from "~/utils";
import { ApiSliceParams } from "../interfaces";
import { createListController } from "../utils";
import {
    ProInvoice,
    ProInvoiceCreateRequest,
    ProInvoiceFields,
    ProInvoiceListFilter,
    ProInvoiceRowOpenItem,
    ProInvoiceState,
    ProInvoiceTextRow,
} from "./interfaces";
import { makeRow } from "./row";

const STUB_SIGNAL = computed((): ProInvoice[] => []);

const getListKey = ({ project, printed }: ProInvoiceListFilter) =>
    `${project}-${printed ? "printed" : "unprinted"}`;

const isPrinted = (invoice: Pick<ProInvoice, "printed_user">) =>
    Boolean(invoice.printed_user);

const viewRow = (item: ProInvoiceRowOpenItem): ProInvoiceTextRow => ({
    ...item,
    _id: item.id,
    id: item.id.toString(),
});

const sortRows = (a: ProInvoiceTextRow, b: ProInvoiceTextRow) =>
    a.sort - b.sort;

export const makeProInvoiceText = ({ connection, company }: ApiSliceParams) => {
    const row = makeRow({ connection, company });
    const projectInvoices: Record<string, ReadonlySignal<ProInvoice[]>> = {};
    const stateStore: Record<string, ProInvoiceState> = {};
    const openSubscriptions: Record<
        string,
        ICachingStream<Readonly<InvoiceOpenItem>>
    > = {};

    const getState = (id: string) => {
        if (!stateStore[id]) {
            stateStore[id] = {
                params: signal(null),
                columns: signal(null),
                rows: signal([]),
                loading: signal(false),
                info: signal(null),
                infoLoading: signal(false),
            };
        }

        return stateStore[id];
    };

    const view = (invoice: InvoiceGet): ProInvoice => {
        const id = invoice.id.toString();

        return {
            ...invoice,
            ...getState(id),
            id,
            _id: invoice.id,
        };
    };

    const {
        items: allInvoices,
        loading,
        loaded,
        listen,
        get,
        getOrLoad,
        whenSingleLoaded,
    } = createListController<InvoiceGet, ProInvoice, ProInvoiceListFilter>({
        connection,
        list: (con, filter) =>
            con.proInvoiceText.items({
                company: company.value,
                project: Ulid.fromString(filter.project),
                printed: filter.printed ?? false,
            }),
        getKey: getListKey,
        load: (con, id) =>
            con.proInvoiceText.get({
                company: company.value,
                id: Array.isArray(id) ? id : [id],
            }),
        sort: (a, b) => a.number - b.number,
        view,
        name: "pro-invoice-text",
    });

    const invoices = (filter: ProInvoiceListFilter) => {
        if (!filter.project) {
            return STUB_SIGNAL;
        }

        const key = getListKey(filter);
        const printed = filter.printed ?? false;

        if (!(key in projectInvoices)) {
            projectInvoices[key] = computed(() =>
                allInvoices.value.filter(
                    (invoice) =>
                        invoice.project.toString() === filter.project &&
                        isPrinted(invoice) === printed,
                ),
            );
        }

        listen(filter);

        return projectInvoices[key];
    };

    const addOpenItems = (
        state: ProInvoiceState,
        items: readonly Readonly<InvoiceOpenItem>[],
    ) => {
        const rows: ProInvoiceTextRow[] = [];

        for (const item of items) {
            if (item.t === "params") {
                state.params.value = item.data;
            } else if (item.t === "columns") {
                state.columns.value = { ...item.data, _id: item.id };
            } else {
                rows.push(viewRow(item));
            }
        }

        if (rows.length) {
            state.rows.value = upsert(
                state.rows.peek().slice(),
                rows,
                "id",
            ).sort(sortRows);
        }
    };

    const removeOpenItems = (
        state: ProInvoiceState,
        items: readonly Readonly<InvoiceOpenItem>[],
    ) => {
        const rowIds: string[] = [];

        for (const item of items) {
            if (item.t === "params") {
                state.params.value = null;
            } else if (item.t === "columns") {
                state.columns.value = null;
            } else {
                rowIds.push(item.id.toString());
            }
        }

        if (rowIds.length) {
            state.rows.value = state.rows
                .peek()
                .filter((r) => !rowIds.includes(r.id));
        }
    };

    const listenOpen = async (
        state: ProInvoiceState,
        sub: ICachingStream<Readonly<InvoiceOpenItem>>,
    ) => {
        try {
            for await (const event of sub) {
                batch(() => {
                    if (event.kind === SubscribeState.Remove) {
                        removeOpenItems(state, event.values);
                    } else if (
                        event.kind === SubscribeState.Add ||
                        event.kind === SubscribeState.Update
                    ) {
                        addOpenItems(state, event.values);
                    } else if (
                        event.kind === SubscribeState.Loaded ||
                        event.kind === SubscribeState.LoadedCache ||
                        event.kind === SubscribeState.Error
                    ) {
                        state.loading.value = false;
                    }
                });
            }
        } catch (error: unknown) {
            console.error("[api] proInvoiceText open error", error);
        }

        state.loading.value = false;
    };

    const loadInfo = async (invoice: ProInvoice) => {
        const state = getState(invoice.id);

        state.infoLoading.value = true;

        try {
            for await (const item of connection.value.proInvoiceText.info({
                company: company.value,
                id: [invoice._id],
            })) {
                state.info.value = item;
            }
        } finally {
            state.infoLoading.value = false;
        }
    };

    const close = (invoice: ProInvoice) => {
        const sub = openSubscriptions[invoice.id];

        if (!sub) {
            return;
        }

        sub.unsub();
        delete openSubscriptions[invoice.id];

        const state = getState(invoice.id);

        batch(() => {
            state.params.value = null;
            state.columns.value = null;
            state.rows.value = [];
            state.loading.value = false;
        });
    };

    // streams are kept open once started: the communication library reuses a
    // dead shared subscription when the same invoice is opened again after
    // closing, so a reopened stream never gets `Loaded` nor live updates
    //
    const open = (invoice: ProInvoice) => {
        if (openSubscriptions[invoice.id]) {
            return;
        }

        const state = getState(invoice.id);
        const sub = connection.value.proInvoiceText.open({
            company: company.value,
            id: invoice._id,
        });

        openSubscriptions[invoice.id] = sub;
        state.loading.value = true;
        listenOpen(state, sub);
        loadInfo(invoice).catch((error: unknown) => {
            console.error("[api] proInvoiceText info error", error);
        });
    };

    const create = ({ project, ...rest }: ProInvoiceCreateRequest) =>
        connection.value.proInvoiceText.create({
            company: company.value,
            project,
            ...rest,
        });

    const update = async (invoice: ProInvoice, fields: ProInvoiceFields) => {
        const result = await connection.value.proInvoiceText.update({
            company: company.value,
            id: invoice._id,
            etag: invoice.etag,
            ...fields,
        });

        if (fields.instructions !== undefined) {
            await loadInfo(invoice);
        }

        return result;
    };

    const remove = async (invoice: ProInvoice) => {
        close(invoice);

        await connection.value.proInvoiceText.delete({
            company: company.value,
            id: invoice._id,
            etag: invoice.etag,
        });
    };

    return {
        row,
        allInvoices,
        invoices,
        loading,
        loaded,
        get,
        getOrLoad,
        whenSingleLoaded,
        open,
        close,
        loadInfo,
        create,
        update,
        remove,
    };
};
